import { cloneElement, useState } from 'react'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { DraggableContentBlockItem, ContentBlockItem } from './DraggableContentBlockItem'

interface Props {
  blocks: ContentBlockItem[]
  onReorder: (blocks: ContentBlockItem[]) => void
  onRemove: (blockId: number) => void
  onEdit?: (blockId: number) => void
  /** Full-size only: deselect the open block (in-overlay Close ✕ → page settings). */
  onClose?: () => void
  /** Render previews at realistic full size (page editor) instead of thumbnails. */
  fullSize?: boolean
  /** The id of the block currently open in the editor — highlights that card. */
  selectedBlockId?: number | 'new' | null
  /** Called after an inline description edit (full-size); passes the merged block. */
  onDescriptionSaved?: (block: ContentBlockItem) => void
  /** Stage an inline description edit into the page's per-block draft (full-size only). */
  onStageDescription?: (blockId: number, language: string, value: string) => void
  /** Stage an inline title edit into the page's per-block draft (full-size only). */
  onStageName?: (blockId: number, language: string, value: string) => void
}

export function DraggableContentBlockList({
  blocks,
  onReorder,
  onRemove,
  onEdit,
  onClose,
  fullSize,
  selectedBlockId,
  onDescriptionSaved,
  onStageDescription,
  onStageName,
}: Props) {
  const [key, setKey] = useState(0)

  const onDragEnd = (result: any) => {
    try {
      if (!result.destination) return
      const sourceIndex = result.source.index
      const destinationIndex = result.destination.index

      const reordered = Array.from(blocks)
      const [removed] = reordered.splice(sourceIndex, 1)
      reordered.splice(destinationIndex, 0, removed)

      const updated = reordered.map((block, index) => ({
        ...block,
        arrangement: index,
      }))

      onReorder(updated)
      setKey(prevKey => prevKey + 1)
    } catch (error) {
      console.error('Drag error:', error)
    }
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId="droppable-content-blocks"
        direction="vertical"
        ignoreContainerClipping={true}
      >
        {provided => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            key={key}
            className="space-y-2"
          >
            {blocks.map((block, index) => (
              <DraggableContentBlockItem
                key={block.id}
                block={block}
                index={index}
                onRemove={onRemove}
                onEdit={onEdit}
                onClose={onClose}
                fullSize={fullSize}
                selected={selectedBlockId === block.id}
                onDescriptionSaved={onDescriptionSaved}
                onStageDescription={onStageDescription}
                onStageName={onStageName}
              />
            ))}
            {provided.placeholder
              ? cloneElement(provided.placeholder, { key: 'rbd-placeholder' })
              : provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}
